import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { caseAPI } from '../api';
import Logo from '../components/Logo';

export default function Home() {
  const [receiptNumber, setReceiptNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleCheck = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);

    try {
      const response = await caseAPI.checkCase(receiptNumber);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check case status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Logo />
          <nav className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pricing')}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/login')}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Login
            </button>
            <button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-indigo-700 hover:to-purple-700 text-sm font-medium whitespace-nowrap h-10"
            >
              Sign Up
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3 leading-tight">
            Stop checking your USCIS case 10 times a day
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Get automatic updates, simple explanations, and alerts when anything changes.
          </p>
        </div>

        {/* Free Check Form */}
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-8 mb-4" data-testid="free-check-form">
          <h2 className="text-2xl font-semibold mb-4 text-center">Check your USCIS case now</h2>
          <form onSubmit={handleCheck} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                USCIS Receipt Number
              </label>
              <input
                type="text"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value.toUpperCase())}
                placeholder="e.g., IOE0912345678"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
                data-testid="receipt-number-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Format: 3 letters + 10 digits (e.g., IOE0912345678)
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="check-case-button"
            >
              {loading ? 'Checking...' : 'Check my USCIS case now'}
            </button>
            <p className="text-center text-sm text-gray-500">
              No account required - takes 5 seconds
            </p>
          </form>
        </div>

        {/* Trust Badge */}
        <div className="text-center text-sm text-gray-500 mb-12">
          <p>Over 1,200 case checks today</p>
        </div>

        {/* Result Display */}
        {result && (
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="case-result">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {result.official_status_title}
                </h3>
                <p className="text-sm text-gray-500">Receipt: {result.receipt_number}</p>
                <p className="text-xs text-gray-400 mt-1">Last checked: just now</p>
              </div>

              {/* AI Explanation */}
              <div className="space-y-6">
                <div className="bg-indigo-50 rounded-lg p-6">
                  <h4 className="font-semibold text-indigo-900 mb-2">Simple explanation</h4>
                  <p className="text-gray-700">{result.ai_explanation.short_summary}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">What it means</h4>
                  <p className="text-gray-700">{result.ai_explanation.what_it_means}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Next step</h4>
                  <p className="text-gray-700">{result.ai_explanation.next_step}</p>
                </div>

                <div className="flex items-center space-x-6">
                  <div>
                    <span className="text-sm text-gray-500">Urgency:</span>
                    <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${
                      result.ai_explanation.urgency_level === 'high' ? 'bg-red-100 text-red-800' :
                      result.ai_explanation.urgency_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {result.ai_explanation.urgency_level}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Phase:</span>
                    <span className="ml-2 text-sm font-medium">{result.ai_explanation.estimated_phase}</span>
                  </div>
                </div>
              </div>

              {/* Official USCIS Text */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Official USCIS Status</h4>
                <p className="text-sm text-gray-600">{result.official_status_text}</p>
              </div>
            </div>

            {/* Warning Block */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-2">Important</h4>
              <p className="text-yellow-800 mb-2">
                USCIS updates can happen anytime. If you don't check regularly, you might miss important changes.
              </p>
              <p className="text-xs text-yellow-700">Last checked: just now</p>
            </div>

            {/* Conversion Block */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-3">Don't miss your next USCIS update</h3>
              <p className="text-indigo-100 mb-6">
                USCIS won't notify you instantly. We check your case every day and alert you as soon as anything changes.
              </p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-300 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>We check your case automatically</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-300 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Get notified the moment anything changes</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-300 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>No more manual checking</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-300 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Track up to 5 cases</span>
                </li>
              </ul>

              <button
                onClick={() => {
                  // Set payment intent before redirecting
                  localStorage.setItem('payment_intent', 'subscribe');
                  navigate('/register');
                }}
                className="w-full bg-white text-indigo-600 py-3 rounded-lg font-semibold hover:bg-gray-100 mb-2"
                data-testid="start-tracking-button"
              >
                Start tracking for $9/month
              </button>
              <p className="text-center text-sm text-indigo-100">Cancel anytime</p>
            </div>

            {/* Mobile Sticky CTA */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
              <button
                onClick={() => {
                  localStorage.setItem('payment_intent', 'subscribe');
                  navigate('/register');
                }}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700"
              >
                Start tracking for $9/month
              </button>
            </div>
          </div>
        )}

        {/* Features Section */}
        {!result && (
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Instant Checks</h3>
              <p className="text-gray-600">Get your USCIS case status in seconds</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Simple Explanations</h3>
              <p className="text-gray-600">Plain English explanations of complex status updates</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Email Alerts</h3>
              <p className="text-gray-600">Get notified when your case status changes</p>
            </div>
          </div>
        )}

        {/* Legal Disclaimer */}
        <div className="max-w-3xl mx-auto text-center text-sm text-gray-500 border-t border-gray-200 pt-8">
          <p>
            This tool is for informational purposes only and does not provide legal advice. 
            USCIS is the official source for case decisions and notices.
          </p>
        </div>
      </div>
    </div>
  );
}
